// dependencies
import React from "react";

import { faArchive, faCheck } from "@fortawesome/pro-solid-svg-icons";

// components
import { ButtonStandard, Checkbox, InputWrapper, Text } from "@performyard/py-components";

interface BulkActionsBarProps {
	disabled: boolean;
	selectedCount: number;
	totalCount: number;
	onCompleteClick: () => void;
	onArchiveClick: () => void;
	onSelectAll: () => void;
	onClearSelection: () => void;
	isQuerying: boolean;
	isInitialized: boolean;
	shouldShowSelectAll: boolean;
	shouldShowClearSelection: boolean;
	allPageRowsSelected: boolean;
	onSelectAllPageChange: (value: boolean) => void;
}

const GoalsListBulkActionsBar = ({
	disabled,
	selectedCount,
	totalCount,
	onCompleteClick,
	onArchiveClick,
	onSelectAll,
	onClearSelection,
	isQuerying,
	isInitialized,
	shouldShowSelectAll,
	shouldShowClearSelection,
	allPageRowsSelected,
	onSelectAllPageChange,
}: BulkActionsBarProps) => {
	return (
		<div className="w-[100%-2px] rounded-t border border-b-0 border-solid border-neutral-2 bg-neutral-1">
			<div className="flex h-12 items-center justify-between px-4">
				{/* Left side - Select All checkbox */}
				<div className="flex items-center">
					<InputWrapper id="select-all-bulk" label="Select All" labelPosition="right" disabled={disabled}>
						<Checkbox
							id="select-all-bulk"
							checked={allPageRowsSelected}
							onCheckedChange={(value) => onSelectAllPageChange(value as boolean)}
							disabled={disabled}
						/>
					</InputWrapper>
				</div>

				{/* Center - Selection info */}
				<div className="flex flex-1 items-center justify-center gap-2">
					{selectedCount > 0 && isInitialized && !isQuerying && !disabled && (
						<>
							<Text size="paragraph-3" weight="semi-bold" data-testid="number-of-selections-label">
								{selectedCount} Goal{selectedCount > 1 ? "s" : ""} selected
							</Text>

							{allPageRowsSelected && (
								<>
									<Text size="paragraph-3" weight="semi-bold" color="neutral-7">
										•
									</Text>
									{shouldShowSelectAll && !shouldShowClearSelection && (
										<span
											onClick={onSelectAll}
											className="cursor-pointer text-primary-5 hover:underline"
											data-testid="select-all-action-label"
										>
											<Text size="paragraph-3" weight="semi-bold" color="primary-5">
												Select all {totalCount} goal{totalCount > 1 ? "s" : ""}
											</Text>
										</span>
									)}
									{shouldShowClearSelection && (
										<span
											onClick={onClearSelection}
											className="cursor-pointer text-primary-5 hover:underline"
											data-testid="clear-selection-action-label"
										>
											<Text size="paragraph-3" weight="semi-bold" color="primary-5">
												Clear Selection
											</Text>
										</span>
									)}
								</>
							)}
						</>
					)}
				</div>

				{/* Right side - Action buttons */}
				<div className="flex items-center gap-2">
					<ButtonStandard
						onClick={onCompleteClick}
						disabled={selectedCount < 1 || isQuerying || !isInitialized || disabled}
						variant="outline"
						color="neutral"
						iconLeft={faCheck}
						size="medium"
					>
						Complete
					</ButtonStandard>
					<ButtonStandard
						onClick={onArchiveClick}
						color="neutral"
						disabled={selectedCount < 1 || isQuerying || !isInitialized || disabled}
						variant="outline"
						iconLeft={faArchive}
						size="medium"
					>
						Archive
					</ButtonStandard>
				</div>
			</div>
		</div>
	);
};

export default GoalsListBulkActionsBar;
